import React, { useState, useEffect, useCallback } from 'react';
import { CircuitBreakerSettings as CircuitBreakerSettingsType } from '../../types';
import { apiService } from '../../services/api';

interface CircuitBreakerSettingsProps {
  queueId: number;
  settings?: CircuitBreakerSettingsType;
  onSave?: (settings: Partial<CircuitBreakerSettingsType>) => Promise<void>;
}

interface FormErrors {
  consecutive_failures_threshold?: string;
  auto_reset_cooldown_minutes?: string;
}

/**
 * CircuitBreakerSettings - Form for configuring circuit breaker parameters
 *
 * Settings:
 * - Circuit breaker enabled toggle
 * - Failure rate threshold slider (0.5-1.0)
 * - Consecutive failures threshold input
 * - Auto-reset enabled toggle
 * - Auto-reset cooldown minutes input
 */
const CircuitBreakerSettings: React.FC<CircuitBreakerSettingsProps> = ({
  queueId,
  settings,
  onSave,
}) => {
  // Form state
  const [circuitBreakerEnabled, setCircuitBreakerEnabled] = useState(true);
  const [failureRateThreshold, setFailureRateThreshold] = useState(0.8);
  const [consecutiveFailuresThreshold, setConsecutiveFailuresThreshold] = useState(3);
  const [autoResetEnabled, setAutoResetEnabled] = useState(false);
  const [autoResetCooldownMinutes, setAutoResetCooldownMinutes] = useState(30);

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});

  // Track original values for change detection
  const [originalSettings, setOriginalSettings] = useState<CircuitBreakerSettingsType | null>(null);

  // Initialize form with settings
  useEffect(() => {
    if (settings) {
      setCircuitBreakerEnabled(settings.circuit_breaker_enabled);
      setFailureRateThreshold(settings.failure_rate_threshold);
      setConsecutiveFailuresThreshold(settings.consecutive_failures_threshold);
      setAutoResetEnabled(settings.auto_reset_enabled);
      setAutoResetCooldownMinutes(settings.auto_reset_cooldown_minutes);
      setOriginalSettings(settings);
    }
  }, [settings]);

  // Check if form has changes
  const hasChanges = useCallback(() => {
    if (!originalSettings) return false;
    return (
      circuitBreakerEnabled !== originalSettings.circuit_breaker_enabled ||
      failureRateThreshold !== originalSettings.failure_rate_threshold ||
      consecutiveFailuresThreshold !== originalSettings.consecutive_failures_threshold ||
      autoResetEnabled !== originalSettings.auto_reset_enabled ||
      autoResetCooldownMinutes !== originalSettings.auto_reset_cooldown_minutes
    );
  }, [
    circuitBreakerEnabled,
    failureRateThreshold,
    consecutiveFailuresThreshold,
    autoResetEnabled,
    autoResetCooldownMinutes,
    originalSettings,
  ]);

  // Validate form
  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (consecutiveFailuresThreshold < 1) {
      newErrors.consecutive_failures_threshold = 'Consecutive failures threshold must be at least 1';
    }

    if (autoResetEnabled && autoResetCooldownMinutes < 1) {
      newErrors.auto_reset_cooldown_minutes = 'Auto-reset cooldown must be at least 1 minute';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle slider change with clamping
  const handleFailureRateChange = (value: string) => {
    let numValue = parseFloat(value);
    // Clamp to valid range
    if (numValue < 0.5) numValue = 0.5;
    if (numValue > 1.0) numValue = 1.0;
    setFailureRateThreshold(numValue);
  };

  // Handle consecutive failures change
  const handleConsecutiveFailuresChange = (value: string) => {
    const numValue = parseInt(value, 10);
    setConsecutiveFailuresThreshold(isNaN(numValue) ? 0 : numValue);
    // Show error immediately if invalid, clear if valid
    if (isNaN(numValue) || numValue < 1) {
      setErrors((prev) => ({ ...prev, consecutive_failures_threshold: 'Consecutive failures threshold must be at least 1' }));
    } else {
      setErrors((prev) => ({ ...prev, consecutive_failures_threshold: undefined }));
    }
  };

  // Handle cooldown change
  const handleCooldownChange = (value: string) => {
    const numValue = parseInt(value, 10);
    setAutoResetCooldownMinutes(isNaN(numValue) ? 0 : numValue);
    // Show error immediately if invalid, clear if valid
    if (isNaN(numValue) || numValue < 1) {
      setErrors((prev) => ({ ...prev, auto_reset_cooldown_minutes: 'Auto-reset cooldown must be at least 1 minute' }));
    } else {
      setErrors((prev) => ({ ...prev, auto_reset_cooldown_minutes: undefined }));
    }
  };

  // Handle save
  const handleSave = async () => {
    setSaveMessage(null);

    if (!validate()) {
      return;
    }

    setIsSaving(true);

    try {
      await apiService.updateQueue(queueId, {
        circuit_breaker_enabled: circuitBreakerEnabled,
        failure_rate_threshold: failureRateThreshold,
        consecutive_failures_threshold: consecutiveFailuresThreshold,
        auto_reset_enabled: autoResetEnabled,
        auto_reset_cooldown_minutes: autoResetCooldownMinutes,
      });

      // Update original settings to match current
      setOriginalSettings({
        circuit_breaker_enabled: circuitBreakerEnabled,
        failure_rate_threshold: failureRateThreshold,
        consecutive_failures_threshold: consecutiveFailuresThreshold,
        auto_reset_enabled: autoResetEnabled,
        auto_reset_cooldown_minutes: autoResetCooldownMinutes,
      });

      setSaveMessage({ type: 'success', text: 'Settings saved successfully' });

      // Call optional onSave callback after successful save
      if (onSave) {
        await onSave({
          circuit_breaker_enabled: circuitBreakerEnabled,
          failure_rate_threshold: failureRateThreshold,
          consecutive_failures_threshold: consecutiveFailuresThreshold,
          auto_reset_enabled: autoResetEnabled,
          auto_reset_cooldown_minutes: autoResetCooldownMinutes,
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save settings';
      setSaveMessage({ type: 'error', text: `Failed to save: ${errorMessage}` });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel/reset
  const handleCancel = () => {
    if (originalSettings) {
      setCircuitBreakerEnabled(originalSettings.circuit_breaker_enabled);
      setFailureRateThreshold(originalSettings.failure_rate_threshold);
      setConsecutiveFailuresThreshold(originalSettings.consecutive_failures_threshold);
      setAutoResetEnabled(originalSettings.auto_reset_enabled);
      setAutoResetCooldownMinutes(originalSettings.auto_reset_cooldown_minutes);
      setErrors({});
      setSaveMessage(null);
    }
  };

  // Show loading state if settings not loaded
  if (!settings) {
    return (
      <div className="p-4 text-gray-500 dark:text-gray-400">
        Loading...
      </div>
    );
  }

  const isDisabled = !circuitBreakerEnabled;

  return (
    <div className="space-y-6 p-4">
      {/* Circuit Breaker Enabled Toggle */}
      <div className="flex items-center justify-between">
        <label htmlFor="circuit-breaker-enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Circuit Breaker Enabled
        </label>
        <input
          type="checkbox"
          id="circuit-breaker-enabled"
          checked={circuitBreakerEnabled}
          onChange={(e) => setCircuitBreakerEnabled(e.target.checked)}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
        />
      </div>

      {/* Failure Rate Threshold Slider */}
      <div>
        <label htmlFor="failure-rate-threshold" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Failure Rate Threshold
        </label>
        <div className="mt-1 flex items-center space-x-4">
          <input
            type="range"
            id="failure-rate-threshold"
            min="0.5"
            max="1.0"
            step="0.05"
            value={failureRateThreshold}
            onChange={(e) => handleFailureRateChange(e.target.value)}
            disabled={isDisabled}
            className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <span className="text-sm text-gray-600 dark:text-gray-400 w-12 text-right">
            {Math.round(failureRateThreshold * 100)}%
          </span>
        </div>
      </div>

      {/* Consecutive Failures Threshold */}
      <div>
        <label htmlFor="consecutive-failures-threshold" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Consecutive Failures Threshold
        </label>
        <input
          type="number"
          id="consecutive-failures-threshold"
          min="1"
          value={consecutiveFailuresThreshold}
          onChange={(e) => handleConsecutiveFailuresChange(e.target.value)}
          disabled={isDisabled}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-700"
        />
        {errors.consecutive_failures_threshold && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">
            {errors.consecutive_failures_threshold}
          </p>
        )}
      </div>

      {/* Auto-Reset Enabled Toggle */}
      <div className="flex items-center justify-between">
        <label htmlFor="auto-reset-enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Auto-Reset Enabled
        </label>
        <input
          type="checkbox"
          id="auto-reset-enabled"
          checked={autoResetEnabled}
          onChange={(e) => setAutoResetEnabled(e.target.checked)}
          disabled={isDisabled}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded disabled:opacity-50"
        />
      </div>

      {/* Auto-Reset Cooldown Minutes */}
      <div>
        <label htmlFor="auto-reset-cooldown-minutes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Auto-Reset Cooldown (Minutes)
        </label>
        <input
          type="number"
          id="auto-reset-cooldown-minutes"
          min="1"
          value={autoResetCooldownMinutes}
          onChange={(e) => handleCooldownChange(e.target.value)}
          disabled={isDisabled || !autoResetEnabled}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-700"
        />
        {errors.auto_reset_cooldown_minutes && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">
            {errors.auto_reset_cooldown_minutes}
          </p>
        )}
      </div>

      {/* Save/Cancel Message */}
      {saveMessage && (
        <div
          className={`text-sm ${
            saveMessage.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
          }`}
        >
          {saveMessage.text}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges() || isSaving}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          disabled={!hasChanges()}
          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default CircuitBreakerSettings;
